import { useEffect, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowLeft, Lock } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { RequestDialog } from "../components/RequestDialog";
import { api } from "../lib/api";
import { CATEGORY_META } from "../lib/constants";
import { useI18n } from "../context/I18nContext";

export default function CollectionDetail() {
  const { category } = useParams();
  const navigate = useNavigate();
  const meta = CATEGORY_META[category];
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    if (!meta) return;
    setLoading(true);
    api
      .get("/assets", { params: { category, status: "available" } })
      .then(({ data }) => setAssets(data))
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  }, [category, meta]);

  if (!meta) return <Navigate to="/" replace />;

  return (
    <div className="bg-white overflow-x-hidden">
      <Navbar />

      {/* Category hero */}
      <section data-testid="collection-hero" className="relative h-[70vh] min-h-[520px] w-full overflow-hidden bg-navy grain">
        <motion.img
          key={meta.image}
          src={meta.image}
          alt={meta.title}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/55 to-navy" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.9 }}
            className="font-sans text-gold text-[11px] tracking-luxury uppercase mb-5"
          >
            Off Market — {meta.title}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 1 }}
            data-testid="collection-title"
            className="font-serif text-white text-5xl md:text-7xl leading-none"
          >
            {meta.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="font-sans text-white/70 text-sm md:text-base max-w-xl mt-6 leading-relaxed"
          >
            {meta.tagline}
          </motion.p>
        </div>
      </section>

      {/* Listings */}
      <section className="bg-white py-20 md:py-28 px-6 lg:px-10">
        <div className="max-w-[1400px] mx-auto">
          <button
            onClick={() => navigate("/")}
            data-testid="back-to-home"
            className="flex items-center gap-2 font-sans text-navy/50 text-[11px] tracking-wide-lux uppercase hover:text-gold transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>

          <div className="flex items-end justify-between mb-12">
            <h2 className="font-serif text-navy text-3xl md:text-5xl">
              Available <span className="italic text-gold">selection</span>
            </h2>
            <span data-testid="collection-count" className="font-sans text-navy/40 text-xs tracking-wide-lux uppercase">
              {assets.length} listings
            </span>
          </div>

          {loading ? (
            <p className="font-sans text-navy/40 text-sm">Loading…</p>
          ) : assets.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="collection-empty"
              className="bg-navy grain rounded-2xl py-20 md:py-28 px-8 flex flex-col items-center text-center"
            >
              <Lock className="w-6 h-6 text-gold mb-7" strokeWidth={1.4} />
              <h3 className="font-serif text-white text-3xl md:text-4xl italic max-w-2xl leading-snug">
                {t("coll.emptyTitle")}
              </h3>
              <p className="font-sans text-white/50 text-sm max-w-md mt-6 leading-relaxed">
                {t("coll.emptyBody")}
              </p>
              <button
                onClick={() => setContactOpen(true)}
                data-testid="collection-empty-contact"
                className="mt-10 bg-gold text-navy px-8 py-4 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:brightness-110 transition-all duration-400"
              >
                {t("coll.emptyCta")}
              </button>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {assets.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.8, delay: (i % 3) * 0.1 }}
                  data-testid={`listing-card-${i}`}
                  className="group"
                >
                  <div className="relative h-[300px] md:h-[320px] overflow-hidden rounded-xl mb-5">
                    {a.image_url ? (
                      <>
                        <img
                          src={a.image_url}
                          alt={a.title}
                          className="w-full h-full object-cover scale-110 transition-all duration-700"
                          style={{ filter: "blur(13px) brightness(0.72)" }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-2">
                            <Lock className="w-5 h-5 text-gold" strokeWidth={1.4} />
                            <span className="font-sans text-white/90 text-[10px] tracking-luxury uppercase">Off Market</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-navy grain flex flex-col items-center justify-center text-center px-6">
                        <span className="font-sans text-gold text-[10px] tracking-luxury uppercase mb-3">Off Market</span>
                        <span className="font-serif text-white text-2xl italic">Visuals shared privately</span>
                        <span className="font-sans text-white/40 text-xs mt-2">Available to qualified buyers on request</span>
                      </div>
                    )}
                    <div className="absolute inset-0 ring-1 ring-inset ring-transparent group-hover:ring-gold/60 rounded-xl transition-all duration-500" />
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-navy text-2xl mb-1">{a.type}</h3>
                      <p className="flex items-center gap-1.5 font-sans text-navy/50 text-xs tracking-wide-lux uppercase">
                        <MapPin className="w-3.5 h-3.5 text-gold" /> {a.country}
                      </p>
                    </div>
                    <span className="font-sans text-gold text-[11px] tracking-wide-lux uppercase whitespace-nowrap mt-1.5">
                      {a.price_label}
                    </span>
                  </div>
                  {a.specs?.length > 0 && (
                    <div data-testid={`listing-specs-${i}`} className="flex flex-wrap gap-x-6 gap-y-2 mt-4 border-t border-navy/10 pt-4">
                      {a.specs.map((s) => (
                        <div key={s.label} className="flex flex-col">
                          <span className="font-sans text-navy/40 text-[10px] tracking-wide-lux uppercase">{s.label}</span>
                          <span className="font-sans text-navy text-sm">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {a.highlights?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {a.highlights.slice(0, 3).map((h) => (
                        <span key={h} className="font-sans text-navy/50 text-[10px] tracking-wide-lux uppercase border border-navy/10 rounded px-2.5 py-1">
                          {h}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => setSelected(a)}
                    data-testid={`listing-request-${i}`}
                    className="mt-5 w-full border border-navy/15 text-navy py-3.5 rounded-md font-sans text-[11px] tracking-wide-lux uppercase hover:bg-navy hover:text-white transition-all duration-400"
                  >
                    Request Details
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />

      <RequestDialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        kind="asset_inquiry"
        asset={selected}
        title="Request Details"
      />
      <RequestDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        kind="contact"
        title={t("coll.emptyCta")}
        subtitle={`Off Market — ${meta.title}`}
      />
    </div>
  );
}
